import { useAuth } from '../context/AuthContext';

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  FileText,
  Plug,
  Cpu,
  Rocket,
  Check,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Circle,
  Loader,
  ArrowRight,
  Shield,
  Sparkles,
  AlertCircle,
  Server,
  Brain,
  MessageSquare,
} from 'lucide-react';
import { ONBOARDING_STEPS } from '../mock/platformData';
import type { OnboardingStep } from '../types';

// Initial pre-filled form data for smooth demo experience
const INITIAL_FORM_DATA: Record<string, string> = {
  businessName: 'Radiant Dental Group LLC',
  dba: 'Radiant Dental',
  taxId: '84-9201847',
  region: 'Southeast',
  facilities: '3',
  staffCount: '95',
  ehrVendor: 'Dentrix',
  ehrUrl: 'https://app.dentrix.com/radiant-dental',
  ehrUser: 'admin_radiant',
  ehrPass: '••••••••••••',
  integrationMode: 'Direct API',
  googleAds: 'true',
  goHighLevel: 'true',
  bambooHr: 'true',
  quickbooks: 'true',
  teams: 'true',
  powerBi: 'false',
  enableMarketing: 'true',
  enableReferrals: 'true',
  enableIntake: 'true',
  enableScheduling: 'true',
  enableRcm: 'true',
  enableHr: 'true',
  enableFinance: 'false',
  enableClinical: 'true',
  riskThreshold: 'Medium — Review before execution',
};

// Deployment animation steps
const DEPLOYMENT_STEPS = [
  {
    title: 'Provisioning Azure infrastructure...',
    description: 'Setting up dedicated tenant, KMS key vault, & HIPAA-compliant DB cluster in East US 2',
    icon: Server,
  },
  {
    title: 'Configuring EHR adapter...',
    description: 'Establishing bidirectional Dentrix API bridge & mapping FHIR clinical entities',
    icon: Plug,
  },
  {
    title: 'Deploying AI agents...',
    description: 'Initializing 7 workstream agents with policy boundaries & confidence scoring models',
    icon: Brain,
  },
  {
    title: 'Setting up Teams approvals...',
    description: 'Hooking human-in-the-loop webhooks to MS Teams approval channel',
    icon: MessageSquare,
  },
];

export const OnboardingWizard: React.FC = () => {
  const { user } = useAuth();
  if (user && user.role !== 'portfolio_admin') {
    return (
      <div className="max-w-2xl mx-auto mt-24 text-center">
        <h2 className="text-xl font-bold text-slate-800">Portfolio access only</h2>
        <p className="text-sm text-slate-500 mt-2">
          Onboarding a new business to the Meridian platform is a portfolio-level action.
          Contact your Meridian administrator if a new business needs to be onboarded.
        </p>
      </div>
    );
  }

  const navigate = useNavigate();

  // Core state variables required
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [formData, setFormData] = useState<Record<string, string>>(INITIAL_FORM_DATA);
  const [isDeploying, setIsDeploying] = useState<boolean>(false);
  const [isComplete, setIsComplete] = useState<boolean>(false);

  // Deployment progress step tracking (0 to 4)
  const [deploymentStage, setDeploymentStage] = useState<number>(0);

  // Track completed/visited steps
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set([0]));

  const totalSteps = ONBOARDING_STEPS.length;
  const activeStep = ONBOARDING_STEPS[currentStep];

  useEffect(() => {
    setVisitedSteps((prev) => new Set([...Array.from(prev), currentStep]));
  }, [currentStep]);

  // Handle fake deployment sequence
  useEffect(() => {
    if (!isDeploying) return;

    setDeploymentStage(0);
    const interval = setInterval(() => {
      setDeploymentStage((prev) => {
        if (prev >= DEPLOYMENT_STEPS.length - 1) {
          clearInterval(interval);
          setTimeout(() => {
            setIsDeploying(false);
            setIsComplete(true);
          }, 1000);
          return DEPLOYMENT_STEPS.length;
        }
        return prev + 1;
      });
    }, 1200);

    return () => clearInterval(interval);
  }, [isDeploying]);

  const handleFieldChange = (key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      setIsDeploying(true);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSkip = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  // Step icons for sidebar navigation
  const getStepIcon = (index: number) => {
    switch (index) {
      case 0:
        return Building2;
      case 1:
        return Plug;
      case 2:
        return Server;
      case 3:
        return Cpu;
      case 4:
        return FileText;
      default:
        return Circle;
    }
  };

  // Helper for field rendering
  const renderField = (field: OnboardingStep['fields'][0]) => {
    const value = formData[field.key] ?? '';

    if (field.type === 'toggle') {
      const isChecked = value === 'true';
      return (
        <div
          key={field.key}
          className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-200 hover:border-slate-300 transition-colors"
        >
          <div className="space-y-0.5 pr-3">
            <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 cursor-pointer">
              {field.label}
              {field.required && <span className="text-rose-500 text-xs">*</span>}
            </label>
            <p className="text-xs text-slate-400">
              {isChecked ? 'Active on platform' : 'Disabled'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => handleFieldChange(field.key, isChecked ? 'false' : 'true')}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-mantis-500 focus:ring-offset-2 focus:ring-offset-slate-900 ${
              isChecked ? 'bg-mantis-500' : 'bg-slate-700'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out flex items-center justify-center ${
                isChecked ? 'translate-x-5' : 'translate-x-0'
              }`}
            >
              {isChecked && <Check className="w-3 h-3 text-cyan-600 font-extrabold" />}
            </span>
          </button>
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.key} className="space-y-1.5">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
            {field.label}
            {field.required && <span className="text-rose-500 ml-1">*</span>}
          </label>
          <select
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            className="w-full bg-slate-100/90 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-mantis-500 transition-all cursor-pointer"
          >
            <option value="" disabled>
              Select {field.label}...
            </option>
            {field.options?.map((opt) => (
              <option key={opt} value={opt} className="bg-white text-slate-800">
                {opt}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (field.type === 'url') {
      const cleanUrl = value.replace(/^https?:\/\//, '');
      return (
        <div key={field.key} className="space-y-1.5">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
            {field.label}
            {field.required && <span className="text-rose-500 ml-1">*</span>}
          </label>
          <div className="flex rounded-xl overflow-hidden border border-slate-200 bg-slate-100/90 focus-within:border-cyan-500 focus-within:ring-1 focus-within:ring-mantis-500 transition-all">
            <span className="inline-flex items-center px-3 bg-slate-100 text-slate-400 text-xs border-r border-slate-200 font-mono select-none">
              https://
            </span>
            <input
              type="text"
              value={cleanUrl}
              onChange={(e) =>
                handleFieldChange(
                  field.key,
                  e.target.value ? `https://${e.target.value.replace(/^https?:\/\//, '')}` : ''
                )
              }
              placeholder={field.placeholder ? field.placeholder.replace(/^https?:\/\//, '') : 'app.setworks.com'}
              className="w-full bg-transparent px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none"
            />
          </div>
        </div>
      );
    }

    // Default text & password
    return (
      <div key={field.key} className="space-y-1.5">
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
          {field.label}
          {field.required && <span className="text-rose-500 ml-1">*</span>}
        </label>
        <input
          type={field.type === 'password' ? 'password' : 'text'}
          value={value}
          onChange={(e) => handleFieldChange(field.key, e.target.value)}
          placeholder={field.placeholder || ''}
          className="w-full bg-slate-100/90 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-mantis-500 transition-all"
        />
      </div>
    );
  };

  // Helper count of enabled toggles for review summary
  const countEnabledToggles = (keys: string[]) => keys.filter((k) => formData[k] === 'true').length;

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-12">
      {/* 1. WIZARD HEADER */}
      <div className="bg-slate-50 border border-slate-100 rounded-2xl p-6 shadow-xl relative overflow-hidden">
        {/* Glow background accent */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-mantis-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div>
            <div className="flex items-center space-x-2.5 mb-2">
              <span className="p-2 rounded-xl bg-mantis-50 text-mantis-600 border border-mantis-200">
                <Rocket className="w-5 h-5" />
              </span>
              <span className="text-xs uppercase font-bold tracking-widest text-mantis-600 bg-mantis-50 px-2.5 py-1 rounded-md border border-mantis-200 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> PE Portfolio Expansion
              </span>
              <span className="text-xs font-medium text-slate-400 flex items-center gap-1 bg-white px-2.5 py-1 rounded-md border border-slate-200">
                <Shield className="w-3 h-3 text-emerald-500" /> HIPAA & SOC2 Ready
              </span>
            </div>
            <h1 className="text-2xl font-black text-slate-800 tracking-tight">Onboard New Business</h1>
            <p className="text-xs text-slate-400 mt-1 max-w-2xl leading-relaxed">
              Deploy Meridian&apos;s autonomous agentic platform for a newly acquired healthcare business.
              Configure EHR connectivity, platform integrations, and agent risk parameters.
            </p>
          </div>

          {!isDeploying && !isComplete && (
            <div className="md:text-right shrink-0 bg-slate-100/50 p-3 rounded-xl border border-slate-200 min-w-[200px]">
              <div className="text-xs text-slate-400 font-medium mb-1">Onboarding Progress</div>
              <div className="text-base font-bold text-mantis-600 flex items-center md:justify-end gap-1.5">
                <span>Step {currentStep + 1} of {totalSteps}</span>
                <span className="text-xs text-slate-400">({Math.round(((currentStep + 1) / totalSteps) * 100)}%)</span>
              </div>
            </div>
          )}
        </div>

        {/* Header Progress Bar */}
        {!isDeploying && !isComplete && (
          <div className="mt-5 pt-4 border-t border-slate-100/80">
            <div className="w-full bg-white rounded-full h-2 overflow-hidden p-0.5 border border-slate-200">
              <div
                className="bg-gradient-to-r from-mantis-500 via-cyan-400 to-indigo-500 h-full rounded-full transition-all duration-500 ease-out shadow-sm shadow-cyan-500/50"
                style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* 2. COMPLETION STATE */}
      {isComplete ? (
        <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-2xl space-y-8 animate-fadeIn">
          <div className="text-center max-w-xl mx-auto space-y-3">
            <div className="w-16 h-16 rounded-full bg-emerald-950/80 border-2 border-emerald-500/80 flex items-center justify-center mx-auto text-emerald-500 shadow-xl shadow-emerald-950/60">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-500 bg-emerald-950/60 px-3 py-1 rounded-full border border-emerald-800/60">
              <Sparkles className="w-3.5 h-3.5" /> Platform Deployment Complete
            </div>
            <h2 className="text-2xl font-black text-slate-800">
              {formData.businessName || 'Radiant Dental Group LLC'} is Live!
            </h2>
            <p className="text-xs text-slate-400 leading-relaxed">
              Meridian AI agents and EHR adapters have been provisioned and configured according to your specifications. Real-time operations & approval workflows are now active.
            </p>
          </div>

          {/* Deployment Summary Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-2">
              <div className="flex items-center space-x-2 text-mantis-600">
                <Building2 className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wider">Business Profile</span>
              </div>
              <div className="text-sm font-semibold text-slate-700">{formData.businessName || 'Radiant Dental'}</div>
              <div className="text-xs text-slate-400 space-y-0.5">
                <div>Region: {formData.region || 'Southeast'}</div>
                <div>Facilities: {formData.facilities || '3'} locations</div>
                <div>Staff: {formData.staffCount || '95'} employees</div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-2">
              <div className="flex items-center space-x-2 text-mantis-600">
                <Plug className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wider">EHR Connector</span>
              </div>
              <div className="text-sm font-semibold text-slate-700">{formData.ehrVendor || 'Dentrix'}</div>
              <div className="text-xs text-slate-400 space-y-0.5">
                <div>Mode: {formData.integrationMode || 'Direct API'}</div>
                <div className="truncate">URL: {formData.ehrUrl || 'app.dentrix.com'}</div>
                <div className="text-emerald-500 font-medium">Status: Connected & Validated</div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-2">
              <div className="flex items-center space-x-2 text-mantis-600">
                <Server className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wider">Integrations</span>
              </div>
              <div className="text-sm font-semibold text-slate-700">
                {countEnabledToggles(['googleAds', 'goHighLevel', 'bambooHr', 'quickbooks', 'teams', 'powerBi'])} Platforms Connected
              </div>
              <div className="text-xs text-slate-400 space-y-0.5">
                <div>Teams Approvals: Enabled</div>
                <div>Billing / CRM: Active</div>
                <div className="text-emerald-500 font-medium">Webhooks Active</div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-2">
              <div className="flex items-center space-x-2 text-mantis-600">
                <Cpu className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wider">AI Governance</span>
              </div>
              <div className="text-sm font-semibold text-slate-700">
                {countEnabledToggles([
                  'enableMarketing',
                  'enableReferrals',
                  'enableIntake',
                  'enableScheduling',
                  'enableRcm',
                  'enableHr',
                  'enableFinance',
                  'enableClinical',
                ])} Agents Provisioned
              </div>
              <div className="text-xs text-slate-400 space-y-0.5">
                <div className="truncate">Policy: {formData.riskThreshold || 'Medium Risk'}</div>
                <div className="text-emerald-500 font-medium">Monitoring Active</div>
              </div>
            </div>
          </div>

          <div className="flex justify-center pt-4">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center space-x-2 px-8 py-3 rounded-xl bg-gradient-to-r from-mantis-500 to-redwood-500 text-white font-bold text-sm shadow-xl shadow-mantis-200/50 transition-all transform hover:-translate-y-0.5"
            >
              <span>Go to Dashboard</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : isDeploying ? (
        /* 3. DEPLOYMENT PROGRESS ANIMATION SCREEN */
        <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-2xl space-y-8">
          <div className="text-center max-w-xl mx-auto space-y-3">
            <div className="w-16 h-16 rounded-full bg-mantis-50 border-2 border-mantis-400 flex items-center justify-center mx-auto text-mantis-600 shadow-xl shadow-mantis-200/50 animate-pulse">
              <Loader className="w-8 h-8 animate-spin" />
            </div>
            <h2 className="text-xl font-black text-slate-800">Deploying Meridian Platform...</h2>
            <p className="text-xs text-slate-400">
              Provisioning isolated cloud infrastructure, registering EHR adapters, and configuring autonomous workstream agents for{' '}
              <span className="text-mantis-500 font-semibold">{formData.businessName || 'New Business'}</span>.
            </p>
          </div>

          {/* Deployment Animation Step List */}
          <div className="max-w-2xl mx-auto space-y-4">
            {DEPLOYMENT_STEPS.map((step, idx) => {
              const StepIcon = step.icon;
              const isFinished = idx < deploymentStage;
              const isCurrent = idx === deploymentStage;

              return (
                <div
                  key={step.title}
                  className={`flex items-start space-x-4 p-4 rounded-xl border transition-all duration-300 ${
                    isFinished
                      ? 'bg-white border-emerald-500/40 text-slate-700'
                      : isCurrent
                      ? 'bg-mantis-50 border-mantis-300 text-slate-800 shadow-lg shadow-mantis-200/40'
                      : 'bg-slate-50 border-slate-100 text-slate-400'
                  }`}
                >
                  <div className="pt-0.5 shrink-0">
                    {isFinished ? (
                      <div className="w-7 h-7 rounded-full bg-emerald-950 text-emerald-500 border border-emerald-700/60 flex items-center justify-center">
                        <CheckCircle2 className="w-4 h-4" />
                      </div>
                    ) : isCurrent ? (
                      <div className="w-7 h-7 rounded-full bg-mantis-50 text-mantis-600 border border-mantis-300 flex items-center justify-center">
                        <Loader className="w-4 h-4 animate-spin" />
                      </div>
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-500 border border-slate-200 flex items-center justify-center">
                        <Circle className="w-4 h-4" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-semibold ${isCurrent ? 'text-mantis-500' : isFinished ? 'text-slate-700' : 'text-slate-400'}`}>
                        {step.title}
                      </span>
                      {isFinished && <span className="text-[10px] text-emerald-500 font-mono font-bold">READY</span>}
                      {isCurrent && <span className="text-[10px] text-mantis-600 font-mono font-bold animate-pulse">PROVISIONING...</span>}
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{step.description}</p>
                  </div>

                  <StepIcon className={`w-5 h-5 shrink-0 ${isFinished ? 'text-emerald-500' : isCurrent ? 'text-mantis-600' : 'text-slate-500'}`} />
                </div>
              );
            })}
          </div>

          <div className="max-w-md mx-auto">
            <div className="flex justify-between text-xs text-slate-400 mb-1.5 font-medium">
              <span>Overall Deployment Status</span>
              <span className="text-mantis-600 font-bold">{Math.min(100, Math.round(((deploymentStage + 1) / DEPLOYMENT_STEPS.length) * 100))}%</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden border border-slate-200">
              <div
                className="bg-mantis-500 h-full transition-all duration-500 ease-out"
                style={{ width: `${Math.min(100, Math.round(((deploymentStage + 1) / DEPLOYMENT_STEPS.length) * 100))}%` }}
              />
            </div>
          </div>
        </div>
      ) : (
        /* 4. MAIN WIZARD INTERFACE (Sidebar Step Navigation + Step Content) */
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* STEP NAVIGATION SIDEBAR (4 cols on lg) */}
          <div className="lg:col-span-4 bg-slate-50 border border-slate-100 rounded-2xl p-4 h-fit space-y-2">
            <div className="px-3 py-2 text-[10px] uppercase font-bold tracking-wider text-slate-400 flex items-center justify-between">
              <span>Configuration Steps</span>
              <span className="text-mantis-600">{currentStep + 1} / {totalSteps}</span>
            </div>

            <div className="space-y-1.5">
              {ONBOARDING_STEPS.map((step, idx) => {
                const IconComponent = getStepIcon(idx);
                const isCurrent = idx === currentStep;
                const isVisited = visitedSteps.has(idx);
                const isCompleted = idx < currentStep;

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => {
                      if (isVisited || idx <= currentStep + 1) {
                        setCurrentStep(idx);
                      }
                    }}
                    disabled={!isVisited && idx > currentStep + 1}
                    className={`w-full flex items-center justify-between p-3 rounded-xl border text-left transition-all ${
                      isCurrent
                        ? 'bg-mantis-50 border-mantis-400 text-slate-800 shadow-lg shadow-mantis-200/50'
                        : isCompleted
                        ? 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                        : isVisited
                        ? 'bg-slate-50 border-slate-100 text-slate-500 hover:bg-slate-100/50'
                        : 'bg-white/20 border-slate-100/60 text-slate-500 opacity-60 cursor-not-allowed'
                    }`}
                  >
                    <div className="flex items-center space-x-3 min-w-0">
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 font-bold text-xs ${
                          isCurrent
                            ? 'bg-mantis-500 text-slate-950 shadow-md shadow-cyan-500/40'
                            : isCompleted
                            ? 'bg-emerald-950 text-emerald-500 border border-emerald-700/60'
                            : 'bg-slate-100 text-slate-400 border border-slate-200'
                        }`}
                      >
                        {isCompleted ? <Check className="w-4 h-4 stroke-[3]" /> : idx + 1}
                      </div>

                      <div className="truncate">
                        <div className={`text-xs font-semibold truncate ${isCurrent ? 'text-mantis-500' : 'text-slate-700'}`}>
                          {step.title}
                        </div>
                        <div className="text-[10px] text-slate-400 truncate mt-0.5">
                          {idx === 4 ? 'Final Verification' : `${step.fields.length} Fields`}
                        </div>
                      </div>
                    </div>

                    <IconComponent
                      className={`w-4 h-4 shrink-0 ml-2 ${
                        isCurrent ? 'text-mantis-600' : isCompleted ? 'text-emerald-500' : 'text-slate-500'
                      }`}
                    />
                  </button>
                );
              })}
            </div>

            {/* Quick Helper Box */}
            <div className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-400 space-y-1.5">
              <div className="flex items-center space-x-1.5 text-slate-500 font-semibold text-[11px]">
                <AlertCircle className="w-3.5 h-3.5 text-mantis-600" />
                <span>Onboarding Tip</span>
              </div>
              <p className="text-[11px] leading-relaxed text-slate-400">
                You can adjust agent risk thresholds and integrations post-launch anytime in Settings.
              </p>
            </div>
          </div>

          {/* MAIN STEP CONTENT AREA (8 cols on lg) */}
          <div className="lg:col-span-8 bg-slate-50 border border-slate-100 rounded-2xl p-6 shadow-xl flex flex-col justify-between space-y-6">
            <div className="space-y-6">
              {/* Step Title Header */}
              <div className="border-b border-slate-100 pb-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-bold font-mono text-mantis-600 uppercase tracking-widest bg-mantis-50 px-2 py-0.5 rounded border border-mantis-200">
                      Step {currentStep + 1}
                    </span>
                    <h2 className="text-lg font-bold text-slate-800">{activeStep.title}</h2>
                  </div>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">{activeStep.description}</p>
              </div>

              {/* DYNAMIC STEP CONTENT */}
              {currentStep === 4 ? (
                /* STEP 5: REVIEW STEP SUMMARY */
                <div className="space-y-6">
                  <div className="p-4 rounded-xl bg-mantis-50/30 border border-cyan-800/50 flex items-start space-x-3">
                    <Sparkles className="w-5 h-5 text-mantis-600 shrink-0 mt-0.5" />
                    <div className="text-xs space-y-1">
                      <div className="font-bold text-mantis-600">Ready to Deploy Meridian Platform</div>
                      <div className="text-slate-500">
                        Review your configured parameters below. Clicking &apos;Deploy Platform&apos; will initiate real-time infrastructure provisioning and agent activation.
                      </div>
                    </div>
                  </div>

                  {/* Section 1: Business Profile */}
                  <div className="bg-slate-50 border border-slate-200/70 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                      <div className="flex items-center space-x-2 text-mantis-600">
                        <Building2 className="w-4 h-4" />
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                          1. Business Information
                        </span>
                      </div>
                      <button
                        onClick={() => setCurrentStep(0)}
                        className="text-xs text-mantis-600 hover:text-mantis-500 font-semibold flex items-center space-x-1"
                      >
                        <FileText className="w-3 h-3" />
                        <span>Edit Section</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                      <div>
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Legal Name</span>
                        <span className="text-slate-700 font-semibold">{formData.businessName || '—'}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">DBA</span>
                        <span className="text-slate-700">{formData.dba || '—'}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Tax ID / EIN</span>
                        <span className="text-slate-700 font-mono">{formData.taxId || '—'}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Region</span>
                        <span className="text-slate-700">{formData.region || '—'}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Facilities</span>
                        <span className="text-slate-700">{formData.facilities || '0'} locations</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Total Staff</span>
                        <span className="text-slate-700">{formData.staffCount || '0'} staff</span>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: EHR Configuration */}
                  <div className="bg-slate-50 border border-slate-200/70 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                      <div className="flex items-center space-x-2 text-mantis-600">
                        <Plug className="w-4 h-4" />
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                          2. EHR Selection & Adapter
                        </span>
                      </div>
                      <button
                        onClick={() => setCurrentStep(1)}
                        className="text-xs text-mantis-600 hover:text-mantis-500 font-semibold flex items-center space-x-1"
                      >
                        <FileText className="w-3 h-3" />
                        <span>Edit Section</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                      <div>
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">EHR Vendor</span>
                        <span className="text-slate-700 font-semibold">{formData.ehrVendor || '—'}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Mode</span>
                        <span className="text-slate-700">{formData.integrationMode || '—'}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">Admin User</span>
                        <span className="text-slate-700 font-mono">{formData.ehrUser || '—'}</span>
                      </div>
                      <div className="col-span-2 md:col-span-3">
                        <span className="text-slate-400 block text-[10px] uppercase font-bold">EHR Endpoint URL</span>
                        <span className="text-slate-700 font-mono truncate block">{formData.ehrUrl || '—'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Integrations */}
                  <div className="bg-slate-50 border border-slate-200/70 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                      <div className="flex items-center space-x-2 text-mantis-600">
                        <Server className="w-4 h-4" />
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                          3. Platform Integrations
                        </span>
                      </div>
                      <button
                        onClick={() => setCurrentStep(2)}
                        className="text-xs text-mantis-600 hover:text-mantis-500 font-semibold flex items-center space-x-1"
                      >
                        <FileText className="w-3 h-3" />
                        <span>Edit Section</span>
                      </button>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {[
                        { key: 'googleAds', label: 'Google Ads' },
                        { key: 'goHighLevel', label: 'GoHighLevel' },
                        { key: 'bambooHr', label: 'BambooHR' },
                        { key: 'quickbooks', label: 'QuickBooks' },
                        { key: 'teams', label: 'MS Teams Approvals' },
                        { key: 'powerBi', label: 'Power BI Analytics' },
                      ].map((item) => {
                        const enabled = formData[item.key] === 'true';
                        return (
                          <span
                            key={item.key}
                            className={`px-2.5 py-1 rounded-lg text-xs font-medium border flex items-center space-x-1.5 ${
                              enabled
                                ? 'bg-mantis-50 text-mantis-500 border-mantis-200'
                                : 'bg-slate-100/50 text-slate-400 border-slate-200/40 line-through'
                            }`}
                          >
                            {enabled ? <Check className="w-3 h-3 text-mantis-600" /> : <Circle className="w-3 h-3" />}
                            <span>{item.label}</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* Section 4: AI Agents */}
                  <div className="bg-slate-50 border border-slate-200/70 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                      <div className="flex items-center space-x-2 text-mantis-600">
                        <Cpu className="w-4 h-4" />
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                          4. Agent Suite & Risk Boundary
                        </span>
                      </div>
                      <button
                        onClick={() => setCurrentStep(3)}
                        className="text-xs text-mantis-600 hover:text-mantis-500 font-semibold flex items-center space-x-1"
                      >
                        <FileText className="w-3 h-3" />
                        <span>Edit Section</span>
                      </button>
                    </div>

                    <div className="space-y-2">
                      <div className="text-xs">
                        <span className="text-slate-400 font-semibold mr-2">Default Risk Boundary:</span>
                        <span className="text-amber-300 font-bold bg-amber-50 px-2 py-0.5 rounded border border-amber-800/60">
                          {formData.riskThreshold || 'Medium Risk'}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-2 pt-1">
                        {[
                          { key: 'enableMarketing', label: 'Marketing Agent' },
                          { key: 'enableReferrals', label: 'Referrals Agent' },
                          { key: 'enableIntake', label: 'Intake Agent' },
                          { key: 'enableScheduling', label: 'Scheduling Agent' },
                          { key: 'enableRcm', label: 'RCM Agent' },
                          { key: 'enableHr', label: 'HR Agent' },
                          { key: 'enableFinance', label: 'Finance Agent' },
                          { key: 'enableClinical', label: 'Clinical Ops Agent' },
                        ].map((item) => {
                          const enabled = formData[item.key] === 'true';
                          return (
                            <span
                              key={item.key}
                              className={`px-2.5 py-1 rounded-lg text-xs font-medium border flex items-center space-x-1.5 ${
                                enabled
                                  ? 'bg-emerald-950/60 text-emerald-300 border-emerald-800/60'
                                  : 'bg-slate-100/50 text-slate-400 border-slate-200/40'
                              }`}
                            >
                              {enabled ? <Check className="w-3 h-3 text-emerald-500" /> : <Circle className="w-3 h-3" />}
                              <span>{item.label}</span>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* STEPS 1 to 4 FORM FIELDS */
                <div
                  className={
                    activeStep.fields.some((f) => f.type === 'toggle')
                      ? 'grid grid-cols-1 md:grid-cols-2 gap-4'
                      : 'grid grid-cols-1 md:grid-cols-2 gap-5'
                  }
                >
                  {activeStep.fields.map((field) => {
                    if (field.key === 'riskThreshold') {
                      return (
                        <div key={field.key} className="md:col-span-2 pt-2">
                          {renderField(field)}
                        </div>
                      );
                    }
                    return renderField(field);
                  })}
                </div>
              )}
            </div>

            {/* STEP NAVIGATION BUTTONS */}
            <div className="pt-6 border-t border-slate-100 flex items-center justify-between">
              <button
                type="button"
                onClick={handlePrevious}
                disabled={currentStep === 0}
                className={`inline-flex items-center space-x-2 px-4 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                  currentStep === 0
                    ? 'bg-slate-50 text-slate-500 border border-slate-100 cursor-not-allowed'
                    : 'bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-100 hover:text-slate-800'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                <span>Previous</span>
              </button>

              <div className="flex items-center space-x-3">
                {currentStep < 4 && (
                  <button
                    type="button"
                    onClick={handleSkip}
                    className="text-xs text-slate-400 hover:text-slate-700 font-medium px-3 py-2 transition-colors"
                  >
                    Skip Optional Fields
                  </button>
                )}

                {currentStep === 4 ? (
                  <button
                    type="button"
                    onClick={() => setIsDeploying(true)}
                    className="inline-flex items-center space-x-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-mantis-500 to-redwood-500 text-white font-bold text-xs shadow-lg shadow-mantis-200/50 transition-all transform hover:-translate-y-0.5"
                  >
                    <Rocket className="w-4 h-4" />
                    <span>Deploy Platform</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="inline-flex items-center space-x-2 px-5 py-2.5 rounded-xl bg-mantis-600 hover:bg-mantis-500 text-white font-bold text-xs shadow-md shadow-mantis-200/50 transition-all"
                  >
                    <span>Next Step</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
